"use client";

import { useTranslations } from "next-intl";
import { BotIcon, DatabaseIcon, LayersIcon, MessageSquareIcon } from "lucide-react";
import { AkiliLogo } from "@/components/onboarding/provider-icons";
import { cn } from "@/lib/utils";

/**
 * ONB-062 (finding F2) / ACC-013 (finding S15) — the brand half of the
 * split account shell, shared by `/signup` and `/auth`.
 *
 * ── ACC-013: why this is no longer named `SignupBrandPanel` ───────────────
 *
 * `/auth` adopted this same shell (ACC-013), so this panel is now the brand
 * surface of BOTH halves of one flow. It was called `SignupBrandPanel` in
 * `signup-brand-panel.tsx`, and a `Signup*` name rendered on the SIGN-IN
 * page is the kind of thing the next reader has to stop and disprove.
 * Renamed, not aliased: `signup-split-shell.tsx` keeps compatibility
 * re-exports for the three routes that import the old constant NAMES, but a
 * component with two names is the drift this ticket exists to remove.
 *
 * The `SignupPage.brand.*` TRANSLATION namespace is deliberately unchanged —
 * see `account-shell.tsx`'s header for why moving it is a separate
 * piece of work.
 *
 * The defect this closes: `/signup` asked a stranger for an email address
 * without ever saying what the product is. For an unfamiliar brand that is
 * the whole of F2 — the page promised nothing at all.
 *
 * ACC-013 found the sign-in half in a worse state than that. `/auth` DID
 * have a brand panel, and it was `hidden lg:flex` — so below 1024px it
 * rendered nothing at all, and every phone got the same silence `/signup`
 * used to give. That panel is gone; this one degrades instead of vanishing
 * (see `BRAND_PART_MIN_WIDTH`), which is the whole difference.
 *
 * Everything here is verified against the shipped product, not written as
 * marketing:
 *
 *   - The four surfaces are four real routes with real UI, and each
 *     description is that surface's own in-app subtitle:
 *       Chats     → `/` (components/layout/sidebar.tsx nav item 1)
 *       Agents    → `/agents` ("Custom AI agents with tailored instructions
 *                   and capabilities" — components/agents/agents-list.tsx)
 *       Knowledge → `/knowledge` ("Manage knowledge bases and documents your
 *                   agents can search and reference" —
 *                   components/knowledge/knowledge-page.tsx). Ships with a
 *                   visible BETA badge in-product, so it carries one here
 *                   too: a surface that is beta inside the app cannot be
 *                   presented as finished on the page that sells it.
 *       Studio    → `/studio` ("AI-generated presentations, documents, and
 *                   more" — app/(site)/studio/studio-client.tsx)
 *     The icons are the same lucide marks the sidebar uses for each, so the
 *     signup page and the app agree visually as well as verbally.
 *
 *     Deliberately NOT claimed as "the four things Akili does": the web
 *     sidebar has seven top-level surfaces (Apps, Library and Marketplace
 *     are the other three). These four are the four the mobile app leads
 *     with and the four with the most substance behind them; the copy says
 *     what they are, it does not say they are all there is.
 *
 *   - The posture badges are what the repo can actually evidence.
 *     "SOC 2" — which the design asked for — is NOT here and must not be
 *     added: there is no certification, no audit, no report and not even a
 *     roadmap commitment anywhere in this monorepo. The only "SOC 2" string
 *     in the entire repo is a sample LLM prompt in a test fixture. Shipping
 *     it would be a fabricated certification claim on the signup page of a
 *     product sold on data sovereignty.
 *       Self-host → docker-compose.yml (+ .minimal/.ollama), k8s/ kustomize
 *                   manifests, INSTALL.md "run everything via Docker
 *                   Compose". (No Helm chart exists — do not promise one.)
 *       Azure     → infra/main.bicep + infra/modules/*, INSTALL.md's Azure
 *                   deploy path, Azure OpenAI / AI Foundry / Key Vault /
 *                   Blob / Entra ID all first-class in .env.example.
 *       No model training → already a published commitment in the Privacy
 *                   Policy (messages/en.json PrivacyPage.noModelTraining:
 *                   "We do not use your conversations, documents, or
 *                   personal data to train or fine-tune any AI model").
 *                   A cleared, already-public claim, not a new one.
 *
 * Responsive degradation — see BRAND_PART_MIN_WIDTH below. The panel is
 * never simply hidden: on the narrowest band it is the only thing on screen
 * telling a stranger what they are signing up for.
 */

/**
 * The minimum viewport width (CSS px) at which each part of the brand panel
 * is VISIBLE. This table is the contract; `BRAND_PART_CLASS` below is its
 * Tailwind realisation and `account-brand-panel.spec.tsx` pins the two
 * together so they cannot drift.
 *
 *   1280+  full split shell: everything, including the proof slot
 *    768   iPad portrait — brand panel stacked above the form; the four
 *          surfaces still fit
 *    390   iPhone / most Galaxy — headline (the value line) plus the
 *          posture badges as a compact row
 *    344   folded Galaxy Z Fold outer screen, and iPad Split View at ~320pt
 *          — one line of proof above the form, nothing else
 */
export const BRAND_PART_MIN_WIDTH = {
  posture: 0,
  headline: 360,
  surfaces: 768,
  quote: 1280,
} as const;

export type BrandPart = keyof typeof BRAND_PART_MIN_WIDTH;

/**
 * Tailwind's JIT scans source text, so these have to be literal strings —
 * they cannot be built from the numbers above. The spec asserts each one
 * contains its own part's width, which is what keeps the two in step.
 *
 * `sr-only` rather than `hidden` for the headline and the surface list: a
 * screen-reader user on a 344px Fold should still be told what the product
 * is. Only the (currently empty) proof slot uses `hidden`, because there is
 * nothing in it to announce.
 */
export const BRAND_PART_CLASS: Record<BrandPart, string> = {
  posture: "",
  headline: "sr-only min-[360px]:not-sr-only",
  surfaces: "sr-only min-[768px]:not-sr-only",
  quote: "hidden min-[1280px]:block",
};

/** Which parts of the brand panel a given viewport width shows. */
export function visibleBrandPartsAt(width: number): BrandPart[] {
  return (Object.keys(BRAND_PART_MIN_WIDTH) as BrandPart[]).filter(
    (part) => width >= BRAND_PART_MIN_WIDTH[part],
  );
}

const SURFACES = [
  { key: "chats", Icon: MessageSquareIcon, beta: false },
  { key: "agents", Icon: BotIcon, beta: false },
  { key: "knowledge", Icon: DatabaseIcon, beta: true },
  { key: "studio", Icon: LayersIcon, beta: false },
] as const;

const POSTURE_KEYS = ["selfHost", "azure", "noModelTraining"] as const;

export function AccountBrandPanel({ className }: { className?: string }) {
  // ACC-013 — still `SignupPage`, on purpose, on a component that now also
  // renders on `/auth`. The copy itself is route-neutral (it describes the
  // product, not the act of signing up), and moving the key tree to a
  // neutral namespace means editing all TEN `messages/*.json` catalogues,
  // which three other lanes are editing right now and where a lost update is
  // silent. The namespace move is a mechanical i18n ticket with no user-
  // visible effect; doing it here would put the riskiest edit in the change
  // with the least to gain from it. See `account-shell.tsx`'s header.
  const t = useTranslations("SignupPage");

  return (
    <aside
      data-testid="account-brand-panel"
      className={cn(
        // The design's gradient. Fixed hexes, not theme tokens: this panel
        // is dark in both themes on purpose — it is the brand surface, and
        // a light-mode version of it would read as a different product.
        "bg-[linear-gradient(160deg,#0B1B3F_0%,#2A1150_100%)] text-white",
        // `relative`/`overflow-hidden` scope the glow below; `isolate` keeps
        // it from blending with whatever the shell paints behind the panel.
        "relative isolate overflow-hidden",
        "flex flex-col justify-center px-4 py-6 min-[360px]:px-6 min-[768px]:px-10 min-[768px]:py-12 min-[1280px]:px-14",
        className,
      )}
    >
      {/*
        Depth, so the panel reads as lit rather than as a flat fill.

        Two soft radial blooms in the indigo/violet the marketing site leads
        with, placed off the top-right and bottom-left so the centred column
        sits in the darker middle and the text keeps its contrast. Purely
        decorative: `aria-hidden`, `pointer-events-none`, and behind the
        content via `-z-10`, so it cannot intercept a click on the form side
        of the shell or reach the accessibility tree.

        Deliberately NOT an image or an SVG mesh — two gradients cost no
        request and no layout, and this panel renders on the first paint of
        the page a stranger lands on.
      */}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 -z-10",
          "bg-[radial-gradient(60%_50%_at_85%_0%,rgba(99,102,241,0.35)_0%,transparent_70%),radial-gradient(50%_45%_at_0%_100%,rgba(124,34,245,0.30)_0%,transparent_70%)]",
        )}
      />

      {/*
        The grid, carried over from akili.io.

        The marketing site's hero sits on a faint 64px rule grid, and it is
        the one graphic element a visitor would recognise between the two
        pages — more than any colour, because the gradient reads as "a dark
        blue panel" while the grid reads as "this is the same product".

        Two 1px linear-gradients rather than an SVG or an image: no request,
        no decode, and it tiles at any panel size. `mask-image` fades it out
        toward the bottom so it frames the headline without ruling lines
        through the cards below, which would fight their own hairline borders.

        White at 4% — visible as texture, never as a table. Decorative, so it
        is `aria-hidden` and sits behind the content on `-z-10` with the
        bloom above.
      */}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 -z-10",
          "bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)]",
          "bg-[size:64px_64px]",
          "[mask-image:linear-gradient(to_bottom,black_0%,black_45%,transparent_85%)]",
        )}
      />
      {/*
        The centred column.
        
        Mirrors the form side's own `mx-auto w-full max-w-[420px]`, one step
        wider because this side carries prose. Without it the copy sat flush
        against the left padding of a half-viewport panel — on a 1440px
        screen that is a 700px column of text hugging one edge, with the eye
        given nothing to anchor to. The panel was already vertically centred
        (`justify-center`); this is the horizontal half that was missing.
      */}
      <div className="mx-auto flex w-full max-w-[460px] flex-col gap-6">
      {/*
        The brand mark.

        The form side shows it above "Welcome back", so on the split shell a
        stranger met the logo once, on the pale half, while the brand half
        opened with unattributed prose. Anchoring the column with the same
        mark is what makes the two halves read as one product.
      */}
      {/*
        Wrapped rather than passed through: `AkiliLogo`'s props are
        `{ size?: number }` only, so `aria-hidden`/`className` on it is a type
        error — one that `next build` catches and vitest does not, because the
        test transform strips types without checking them.
      */}
      <span aria-hidden="true" className="shrink-0">
        <AkiliLogo size={44} />
      </span>

      <h1
        data-testid="account-brand-headline"
        className={cn(
          "text-2xl leading-[1.12] font-semibold tracking-tight text-balance min-[768px]:text-[2rem] min-[1280px]:text-[2.5rem]",
          // The marketing hero sets its value line in a gradient. Applied to
          // the whole string rather than one clause: the headline is
          // translated into ten languages, and highlighting any single phrase
          // in it ("human intelligence", say) would mean splitting a sentence
          // whose word order is different in most of them. White into lavender
          // keeps the full contrast at the start of the line where reading
          // begins.
          //
          // `text-transparent` only takes effect once the gradient paints. If
          // `background-clip:text` is unsupported the browser ignores the
          // clip and the fill both, and the text renders in the inherited
          // white — a graceful floor, not an invisible headline.
          "bg-[linear-gradient(100deg,#FFFFFF_0%,#FFFFFF_55%,#C7BFFF_100%)] bg-clip-text text-transparent",
          BRAND_PART_CLASS.headline,
        )}
      >
        {t("brand.headline")}
      </h1>

      <ul
        data-testid="account-brand-surfaces"
        aria-label={t("brand.surfacesLabel")}
        className={cn("flex flex-col gap-2.5", BRAND_PART_CLASS.surfaces)}
      >
        {/*
          Each surface is a card with its own icon tile, rather than an icon
          beside a paragraph.

          The bare list read as body copy: four indistinguishable rows that
          the eye slid past on the way to the form. A hairline border and a
          5%-white fill give each one an edge to land on, and the tile turns
          the lucide mark into a deliberate object instead of a bullet. Same
          treatment the marketing site uses for its two "See it..." cards, so
          the page a stranger arrives from and the page they land on agree.

          `bg-white/[0.06]` over the panel's own gradient — not an opaque
          hex — so the blooms above still read through the cards and the
          panel keeps its depth.
        */}
        {SURFACES.map(({ key, Icon, beta }) => (
          <li
            key={key}
            className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.06] p-3 min-[768px]:gap-4 min-[768px]:p-4"
          >
            <span
              aria-hidden="true"
              className="grid size-9 shrink-0 place-items-center rounded-lg border border-white/10 bg-white/10"
            >
              <Icon className="size-4.5 text-white" />
            </span>
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white">
                {t(`brand.surfaces.${key}.name`)}
                {beta && (
                  <span className="rounded-sm border border-white/30 px-1.5 py-px text-[10px] font-semibold tracking-wide text-white/70 uppercase">
                    {t("brand.beta")}
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-sm leading-snug text-white/70">
                {t(`brand.surfaces.${key}.description`)}
              </p>
            </div>
          </li>
        ))}
      </ul>

      {/*
        The proof slot — deliberately, verifiably EMPTY.

        The design calls for one named-role customer quote here. There is no
        such quote: a repo-wide search for a testimonial, case study,
        reference customer or named-role endorsement of Akili returns
        nothing but the product's OWN testimonial *block type* (a thing
        Akili can generate inside a webpage it authors for a user) and two
        synthetic test fixtures ("It works." — Ada, CTO).

        Nothing is clearable because nothing exists. A fabricated
        testimonial on the signup page of a product sold on data
        sovereignty is not a survivable mistake, so this ships empty and
        the layout is built to hold without it — the panel is a flex column
        with a gap, so an empty slot costs exactly zero height.

        To fill it: put a real, cleared quote (person, role, company, and
        written permission) behind `SignupPage.brand.proof.*` and render it
        here. Do not put a placeholder person in this element.
      */}
      <figure
        data-testid="account-brand-proof"
        data-proof-state="empty"
        className={cn("empty:hidden", BRAND_PART_CLASS.quote)}
      />

      <ul
        data-testid="account-brand-posture"
        aria-label={t("brand.postureLabel")}
        className={cn(
          "flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-medium text-white/70 min-[360px]:gap-x-3 min-[768px]:text-xs",
          BRAND_PART_CLASS.posture,
        )}
      >
        {POSTURE_KEYS.map((key) => (
          <li
            key={key}
            className="rounded-full border border-white/20 px-2 py-0.5 whitespace-nowrap min-[360px]:px-2.5"
          >
            {t(`brand.posture.${key}`)}
          </li>
        ))}
      </ul>
      </div>
    </aside>
  );
}
